import React from 'react';
import ReactDOM from 'react-dom/client';
import { ReactQueryDevtools } from 'react-query/devtools';

import Router from './Router';
import { mswWorker } from './mocks/mswroutes';
import { loadAndApplyOverlay } from './mocks/overlay';
import Providers from './providers/Providers';
import './index.css';
import useUserStore from './store/user';
import { registerAuthListeners } from './auth/auth';
import { useSentry } from './sentry';

import { pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  '/pdf.worker.min.js',
  import.meta.url,
).toString();

// True for any non-production build. Computed once at module load so it
// stays typed as `boolean` and survives TS narrowing of `import.meta.env.MODE`
// inside mode-specific branches below.
const IS_NON_PROD: boolean = import.meta.env.MODE !== 'production';

// Dev-only: primes localStorage from `?seed_session=<base64-json>` before
// React mounts. Auth seeds should set 'mobilix-auth' to Zustand's persisted
// shape: { state: { userProfileId, role, organization, accessToken, permissions }, version: 0 }.
// This skips the login redirect. NB: a seeded JWT expires in <=10 min and
// renewal needs a real Kratos session cookie — fine for short runs only. Param
// is stripped after application.
function applySeedSession(): void {
  if (!IS_NON_PROD) return;
  const raw = new URLSearchParams(window.location.search).get('seed_session');
  if (!raw) return;
  try {
    // `URLSearchParams.get` urldecodes `+` to a space, so callers who paste
    // raw standard base64 lose every `+`. Also accept URL-safe base64
    // (`-`/`_`). Normalize all three forms back to standard base64.
    const normalized = raw.replace(/[- ]/g, '+').replace(/_/g, '/');
    const parsed: unknown = JSON.parse(atob(normalized));
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        const stored = typeof v === 'string' ? v : JSON.stringify(v);
        window.localStorage.setItem(k, stored);
      }
    }
  } catch (err) {
    // Bad URL shouldn't break boot, but a silent no-op is hard to debug;
    // surface it in dev so the source of "nothing happened" is visible.
    console.warn(
      '[dev] applySeedSession: malformed seed_session, ignoring',
      err,
    );
  }
  // The persisted auth store hydrated from localStorage at module-import
  // time, before the seed was written — re-read it now.
  void useUserStore.persist.rehydrate();
  const url = new URL(window.location.href);
  url.searchParams.delete('seed_session');
  window.history.replaceState(null, '', url.toString());
}

//MSW:
async function prepare(): Promise<unknown> {
  applySeedSession();
  if (import.meta.env.MODE === 'msw') {
    document.title += ' (MSW)';

    await mswWorker.start({
      onUnhandledRequest(req) {
        if (
          req.url.startsWith('http://localhost:3000') ||
          req.url.startsWith('http://localhost:3001')
        ) {
          //Requests to 3000 and 3001 are always ui development vite server itself, so always ignore.
          return;
        }
        if (req.url.startsWith('https://maps.g')) {
          //Ignore Google maps `maps.googleapis.com/..` and `maps.gstatic.com/..`
          return;
        }
        console.warn('Unhandled: %s %s', req.method, req.url);
      },
    });
    if (IS_NON_PROD) {
      // Dev-only escape hatch for runtime handler overrides — e.g. e2e
      // scripts calling `window.__msw.use(...)` or `resetHandlers()`.
      (window as unknown as { __msw?: typeof mswWorker }).__msw = mswWorker;
      await loadAndApplyOverlay(mswWorker);
    }
    return undefined;
  }
  return Promise.resolve(undefined);
}

const AppWithSentry: React.FC = () => {
  const { loaded } = useSentry();
  if (!loaded) {
    return null;
  }

  return (
    <Providers>
      <Router />
      <ReactQueryDevtools initialIsOpen={false} />
    </Providers>
  );
};

//MSW:
//ALSO note that there is further custom code inside useApiClient to enable mocking of api auth.
prepare()
  .then(() => {
    // Cross-tab sync + bfcache-restore reload, registered once at boot.
    registerAuthListeners();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <AppWithSentry />
      </React.StrictMode>,
    );
  })
  .catch((err) => {
    throw err;
  });
